import { PipeTransform, Pipe, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

export enum FunctionGroup {
  RotorSetup = 'Rotor setup',
  ReceiveAndShowMeasuringSignal = 'Receive & show measuring signal',
  ControlCabinet = 'Control Cabinet',
  RotorAccelerationDeceleration = 'Rotor acceleration / deceleration',
  OperatorProtection = 'Operator protection',
  RotorPositioningAndCorrection = 'Rotor positioning & correction',
  DocumentAdministration = 'Document administration',
  NoAllocation = 'No allocation',
}

export enum Components {
  Machine = 'Machine',
  MachineBed = 'Machine bed',
  PedestalFoot = 'Pedestal foot',
  PedestalClamp = 'Pedestal clamp',
  PedestalCarriage = 'Pedestal carriage',
  Dynamometer = 'Dynamometer',
  VibrationPickup = 'Vibration pickup',
  RollerInserts = 'Roller inserts',
  SaddleInserts = 'Saddle insert',
  SlidingRollerInserts = 'Sliding roller inserts',
  VPrismInserts = 'V-prism insert',
  CounterBearing = 'Counter bearing',
  AxialCounterThrust = 'Axial counter thrust',
  PneumaticPressureDevice = 'Pneumatic pressure device',
  DripFeedLubrication = 'Drip feed lubrication',
  MeasuringUnit = 'Measuring unit',
  MeasuringUnitMonitor = 'Measuring unit monitor',
  Elektric = 'Electric',
  BeltDrive = 'Beltdrive',
  GearDrive = 'gear drive',
  Motor = 'Motor',
  DriveSpindle = 'Drive spindle',
  ClampingDevice = 'Clamping device',
  MotorBreak = 'Motor break',
  PneumaticTensioningDevice = 'Pneumatic tensioning device',
  IncrementalEncoder = 'Incremental encoder',
  ReferenceMark = 'Reference mark',
  BearingForceMonitoring = 'Bearing force monitoring',
  VibrationMonitoring = 'Vibration monitoring',
  ProtectionDevice = 'Protection device',
  IndexingDevice = 'Indexing device',
  CorrectionUnit = 'Correction unit',
  Printer = 'Printer',
  Keyboard = 'Keyboard',
  TechnicalDocumentation = 'Technical documentation',
  OtherComponent = 'Other components',
}

export const componentToFunctionMap: Map<Components, FunctionGroup> = new Map([
  [Components.MachineBed, FunctionGroup.RotorSetup],
  [Components.PedestalFoot, FunctionGroup.RotorSetup],
  [Components.PedestalClamp, FunctionGroup.RotorSetup],
  [Components.PedestalCarriage, FunctionGroup.RotorSetup],
  [Components.Dynamometer, FunctionGroup.RotorSetup],
  [Components.VibrationPickup, FunctionGroup.RotorSetup],
  [Components.RollerInserts, FunctionGroup.RotorSetup],
  [Components.SaddleInserts, FunctionGroup.RotorSetup],
  [Components.SlidingRollerInserts, FunctionGroup.RotorSetup],
  [Components.VPrismInserts, FunctionGroup.RotorSetup],
  [Components.CounterBearing, FunctionGroup.RotorSetup],
  [Components.AxialCounterThrust, FunctionGroup.RotorSetup],
  [Components.PneumaticPressureDevice, FunctionGroup.RotorSetup],
  [Components.DripFeedLubrication, FunctionGroup.RotorSetup],
  [Components.MeasuringUnit, FunctionGroup.ReceiveAndShowMeasuringSignal],
  [Components.MeasuringUnitMonitor, FunctionGroup.ReceiveAndShowMeasuringSignal],
  [Components.Elektric, FunctionGroup.ControlCabinet],
  [Components.BeltDrive, FunctionGroup.RotorAccelerationDeceleration],
  [Components.GearDrive, FunctionGroup.RotorAccelerationDeceleration],
  [Components.Motor, FunctionGroup.RotorAccelerationDeceleration],
  [Components.DriveSpindle, FunctionGroup.RotorAccelerationDeceleration],
  [Components.ClampingDevice, FunctionGroup.RotorAccelerationDeceleration],
  [Components.MotorBreak, FunctionGroup.RotorAccelerationDeceleration],
  [Components.PneumaticTensioningDevice, FunctionGroup.RotorAccelerationDeceleration],
  [Components.IncrementalEncoder, FunctionGroup.RotorAccelerationDeceleration],
  [Components.ReferenceMark, FunctionGroup.RotorAccelerationDeceleration],
  [Components.BearingForceMonitoring, FunctionGroup.OperatorProtection],
  [Components.VibrationMonitoring, FunctionGroup.OperatorProtection],
  [Components.ProtectionDevice, FunctionGroup.OperatorProtection],
  [Components.IndexingDevice, FunctionGroup.RotorPositioningAndCorrection],
  [Components.CorrectionUnit, FunctionGroup.RotorPositioningAndCorrection],
  [Components.Printer, FunctionGroup.DocumentAdministration],
  [Components.Keyboard, FunctionGroup.DocumentAdministration],
  [Components.TechnicalDocumentation, FunctionGroup.DocumentAdministration],
  [Components.Machine, FunctionGroup.NoAllocation],
  [Components.OtherComponent, FunctionGroup.NoAllocation],
]);

export function getComponentMsgid(component: Components | null): string {
  if (!component) {
    return '';
  }
  return 'COMPONENT_' + component;
}

export function isComponentEnum(component: any): component is Components {
  return Object.values(Components).includes(<any>component);
}

export function translateComponent(value: any, translate: TranslateService): string {
  if (!isComponentEnum(<any>value) && !!value) {
    return value; // Custom, should not be translated
  }

  const msgid = getComponentMsgid(<Components>value);
  if (!msgid) {
    return '';
  }
  return translate.instant(msgid);
}

export function getFunctionGroupMsgid(functionGroup: FunctionGroup | null): string {
  if (!functionGroup) {
    return '';
  }
  return 'FUNCTIONGROUP_' + functionGroup;
}

@Pipe({ name: 'taskFunctionGroup' })
export class TaskFunctionGroupPipe implements PipeTransform {
  private readonly translate = inject(TranslateService);

  static translate(value: any, translate: TranslateService) {
    const msgid = getFunctionGroupMsgid(value);
    if (!msgid) {
      return '';
    }
    return translate.instant(msgid);
  }

  transform(value: FunctionGroup | null): string {
    return TaskFunctionGroupPipe.translate(value, this.translate);
  }
}

@Pipe({ name: 'taskComponent' })
export class TaskComponentPipe implements PipeTransform {
  private readonly translate = inject(TranslateService);

  transform(value: Components | string | null): string {
    return translateComponent(value, this.translate);
  }
}
